import React from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

export interface UnassignedReviewBrowseControlsProps {
  index: number;
  total: number;
  bleed: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  canClaim: boolean;
  claiming: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onClaim: () => Promise<void>;
}

export function UnassignedReviewBrowseControls({
  index,
  total,
  bleed,
  hasPrevious,
  hasNext,
  canClaim,
  claiming,
  onPrevious,
  onNext,
  onClaim,
}: UnassignedReviewBrowseControlsProps) {
  return (
    <div
      role="region"
      aria-label="Unassigned review browser"
      className="sticky top-16 z-20 h-12 w-full border-b border-gray-200 bg-gray-50"
    >
      <div
        role="group"
        aria-label="Unassigned review controls"
        className={bleed
          ? 'flex h-full items-center justify-between gap-4 px-4 sm:px-6 lg:px-8'
          : 'mx-auto flex h-full max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8'}
      >
        <span className="text-sm font-medium text-gray-700">Browse unassigned reviews</span>
        <div className="flex items-center gap-2">
          <span className="whitespace-nowrap text-xs font-medium text-gray-600">
            Unassigned {index + 1} of {total}
          </span>
          <button
            type="button"
            aria-label="Previous unassigned review"
            title="Previous unassigned review"
            onClick={onPrevious}
            disabled={!hasPrevious || claiming}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Next unassigned review"
            title="Next unassigned review"
            onClick={onNext}
            disabled={!hasNext || claiming}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => void onClaim()}
            disabled={!canClaim || claiming}
            className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {claiming && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {claiming ? 'Claiming…' : 'Claim review'}
          </button>
        </div>
      </div>
    </div>
  );
}
